/**
 * useNotificationHub Hook
 * SignalR client for real-time notifications
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  HubConnectionBuilder,
  HubConnection,
  HubConnectionState,
  LogLevel,
  HttpTransportType,
} from '@microsoft/signalr';
import {
  Notification,
  ConnectionState,
  NotificationHubState,
} from '../types/notification';
import { API_BASE_URL } from '../config/api';

// Reconnection delays for exponential backoff (in ms)
const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 30000, 60000];

interface UseNotificationHubOptions {
  autoConnect?: boolean;
  onNotificationReceived?: (notification: Notification) => void;
  onConnectionStateChange?: (state: ConnectionState) => void;
}

interface UseNotificationHubReturn extends NotificationHubState {
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  markNotificationRead: (notificationId: string) => Promise<void>;
}

export function useNotificationHub(
  options: UseNotificationHubOptions = {}
): UseNotificationHubReturn {
  const {
    autoConnect = false,
    onNotificationReceived,
    onConnectionStateChange,
  } = options;

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const connectionRef = useRef<HubConnection | null>(null);
  const reconnectAttemptRef = useRef(0);
  const isConnectingRef = useRef(false);

  // Update connection state and notify callback
  const updateConnectionState = useCallback(
    (state: ConnectionState) => {
      setConnectionState(state);
      onConnectionStateChange?.(state);
    },
    [onConnectionStateChange]
  );

  // Create and configure the SignalR connection
  const createConnection = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return null;
    }

    const hubUrl = `${API_BASE_URL}/hubs/notifications`;

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => localStorage.getItem('accessToken') || '',
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
        withCredentials: true,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff with jitter
          const delayIndex = Math.min(
            retryContext.previousRetryCount,
            RECONNECT_DELAYS.length - 1
          );
          const baseDelay = RECONNECT_DELAYS[delayIndex];
          const jitter = Math.random() * 1000;
          return baseDelay + jitter;
        },
      })
      .configureLogging(LogLevel.None)
      .build();

    return connection;
  }, []);

  // Set up event handlers for the connection
  const setupEventHandlers = useCallback(
    (connection: HubConnection) => {
      // Handle received notifications
      connection.on('ReceiveNotification', (notification: Notification) => {
        setNotifications((prev) => [notification, ...prev]);
        if (!notification.isRead) {
          setUnreadCount((prev) => prev + 1);
        }
        onNotificationReceived?.(notification);
      });

      // Handle unread count updates
      connection.on('UnreadCountUpdated', (count: number) => {
        setUnreadCount(count);
      });

      // Handle notification read state changes
      connection.on('NotificationRead', (notificationId: string) => {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, isRead: true } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      });

      // Handle all notifications read
      connection.on('AllNotificationsRead', () => {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, isRead: true }))
        );
        setUnreadCount(0);
      });

      // Handle connection lifecycle events
      connection.onclose(() => {
        updateConnectionState('disconnected');
      });

      connection.onreconnecting(() => {
        updateConnectionState('reconnecting');
      });

      connection.onreconnected(() => {
        reconnectAttemptRef.current = 0;
        updateConnectionState('connected');
      });
    },
    [onNotificationReceived, updateConnectionState]
  );

  // Connect to the SignalR hub
  const connect = useCallback(async () => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      return;
    }

    // Check if already connected
    if (
      connectionRef.current?.state === HubConnectionState.Connected ||
      connectionRef.current?.state === HubConnectionState.Connecting
    ) {
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    isConnectingRef.current = true;
    updateConnectionState('connecting');

    try {
      // Clean up existing connection if any
      if (connectionRef.current) {
        await connectionRef.current.stop();
      }

      // Create new connection
      const connection = createConnection();
      if (!connection) {
        throw new Error('Failed to create SignalR connection');
      }

      connectionRef.current = connection;
      setupEventHandlers(connection);

      // Start the connection
      await connection.start();
      reconnectAttemptRef.current = 0;
      updateConnectionState('connected');
    } catch (error) {
      updateConnectionState('disconnected');

      // Schedule reconnection attempt with exponential backoff
      const delayIndex = Math.min(
        reconnectAttemptRef.current,
        RECONNECT_DELAYS.length - 1
      );
      const delay = RECONNECT_DELAYS[delayIndex];
      reconnectAttemptRef.current += 1;

      setTimeout(() => {
        if (localStorage.getItem('accessToken')) {
          connect();
        }
      }, delay);
    } finally {
      isConnectingRef.current = false;
    }
  }, [createConnection, setupEventHandlers, updateConnectionState]);

  // Disconnect from the SignalR hub
  const disconnect = useCallback(async () => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
      } catch {
        // Ignore disconnect errors
      } finally {
        connectionRef.current = null;
        updateConnectionState('disconnected');
      }
    }
  }, [updateConnectionState]);

  // Mark a notification as read
  const markNotificationRead = useCallback(async (notificationId: string) => {
    if (connectionRef.current?.state !== HubConnectionState.Connected) {
      return;
    }

    try {
      await connectionRef.current.invoke('MarkNotificationRead', notificationId);
    } catch (error) {
      throw error;
    }
  }, []);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect && localStorage.getItem('accessToken')) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop().catch(console.error);
      }
    };
  }, [autoConnect, connect]);

  // Expose the current connection state
  const isConnected = connectionState === 'connected';

  return {
    isConnected,
    connectionState,
    notifications,
    unreadCount,
    connect,
    disconnect,
    markNotificationRead,
  };
}

export default useNotificationHub;
